import { useMemo, useContext } from 'react';
import { useTheme } from 'foundations-theming-specialty/utils';
import { OrderedMap, List, Map as ImmutableMap } from 'immutable';
import { THEME } from 'reporting-data/tsTypes/visualization/colors';
import { stepGradientColorsArray } from '../color/interpolate';
import { UserInfoContext } from 'reporting-data/userInfo/userInfoContext';
import { gate } from 'hub-http/gates';
import { DO_NOT_USE_DEPRECATED_THEMES } from '../color/themes';
const TOKENIZED_THEMES_GATE = gate('ReportingViz:TokenizedThemes');
export const MULTI_THEME_SETTINGS = [{
  trellisToken: 'reporting-fill-chart-default-light-orange',
  name: `ORANGE`
}, {
  trellisToken: 'reporting-fill-chart-default-light-aqua',
  name: `AQUA`
}, {
  trellisToken: 'reporting-fill-chart-default-light-purple',
  name: `PURPLE`
}, {
  trellisToken: 'reporting-fill-chart-default-light-yellow',
  name: `YELLOW`
}, {
  trellisToken: 'reporting-fill-chart-default-light-pink',
  name: `PINK`
}, {
  trellisToken: 'reporting-fill-chart-default-light-blue',
  name: `BLUE`
}, {
  trellisToken: 'reporting-fill-chart-default-light-green',
  name: `GREEN`
}, {
  trellisToken: 'reporting-fill-chart-default-dark-orange',
  name: `DARK_ORANGE`
}, {
  trellisToken: 'reporting-fill-chart-default-dark-aqua',
  name: `DARK_AQUA`
}, {
  trellisToken: 'reporting-fill-chart-default-dark-purple',
  name: `DARK_PURPLE`
}, {
  trellisToken: 'reporting-fill-chart-default-dark-yellow',
  name: `DARK_YELLOW`
}, {
  trellisToken: 'reporting-fill-chart-default-dark-pink',
  name: `DARK_PINK`
}, {
  trellisToken: 'reporting-fill-chart-default-dark-blue',
  name: `DARK_BLUE`
}, {
  trellisToken: 'reporting-fill-chart-default-dark-green',
  name: `DARK_GREEN`
}];
export const useReportingThemes = () => {
  const theme = useTheme();
  const {
    gates
  } = useContext(UserInfoContext);
  return useMemo(() => {
    if (!(gates !== null && gates !== void 0 && gates.includes(TOKENIZED_THEMES_GATE))) {
      return DO_NOT_USE_DEPRECATED_THEMES;
    }
    const getTrellisTokenHexValue = tokenKey => theme.color[tokenKey] || theme.color['reporting-fill-chart-white'];

    // Tokenized MULTI theme using default chart tokens
    const multiTheme = List(MULTI_THEME_SETTINGS.map(({
      trellisToken
    }) => getTrellisTokenHexValue(trellisToken)));

    // Tokenized gradient themes using scale tokens - 2-point interpolation like ShadesOf palettes
    const orangesTheme = List(stepGradientColorsArray(9, [{
      positionFraction: 0,
      value: getTrellisTokenHexValue('reporting-fill-chart-scale-light-orange')
    }, {
      positionFraction: 1,
      value: getTrellisTokenHexValue('reporting-fill-chart-scale-dark-orange')
    }]));
    const aquasTheme = List(stepGradientColorsArray(9, [{
      positionFraction: 0,
      value: getTrellisTokenHexValue('reporting-fill-chart-scale-light-aqua')
    }, {
      positionFraction: 1,
      value: getTrellisTokenHexValue('reporting-fill-chart-scale-dark-aqua')
    }]));
    const purplesTheme = List(stepGradientColorsArray(9, [{
      positionFraction: 0,
      value: getTrellisTokenHexValue('reporting-fill-chart-scale-light-purple')
    }, {
      positionFraction: 1,
      value: getTrellisTokenHexValue('reporting-fill-chart-scale-dark-purple')
    }]));
    const yellowsTheme = List(stepGradientColorsArray(9, [{
      positionFraction: 0,
      value: getTrellisTokenHexValue('reporting-fill-chart-scale-light-yellow')
    }, {
      positionFraction: 1,
      value: getTrellisTokenHexValue('reporting-fill-chart-scale-dark-yellow')
    }]));
    const pinksTheme = List(stepGradientColorsArray(9, [{
      positionFraction: 0,
      value: getTrellisTokenHexValue('reporting-fill-chart-scale-light-pink')
    }, {
      positionFraction: 1,
      value: getTrellisTokenHexValue('reporting-fill-chart-scale-dark-pink')
    }]));
    const bluesTheme = List(stepGradientColorsArray(9, [{
      positionFraction: 0,
      value: getTrellisTokenHexValue('reporting-fill-chart-scale-light-blue')
    }, {
      positionFraction: 1,
      value: getTrellisTokenHexValue('reporting-fill-chart-scale-dark-blue')
    }]));
    const greensTheme = List(stepGradientColorsArray(9, [{
      positionFraction: 0,
      value: getTrellisTokenHexValue('reporting-fill-chart-scale-light-green')
    }, {
      positionFraction: 1,
      value: getTrellisTokenHexValue('reporting-fill-chart-scale-dark-green')
    }]));

    // Tokenized FUNNEL theme - 2-point interpolation
    const funnelTheme = List(stepGradientColorsArray(10, [{
      positionFraction: 0,
      value: getTrellisTokenHexValue('reporting-fill-chart-default-dark-purple')
    }, {
      positionFraction: 1,
      value: getTrellisTokenHexValue('reporting-fill-chart-default-light-orange')
    }]));

    // PATTERNS theme structure - using tokenized MULTI theme
    const patternsTheme = ImmutableMap({
      colorsTheme: THEME.MULTI,
      patterns: List([
      //Pulled from Highcharts' default patterns: https://github.com/highcharts/highcharts/blob/c11ce666605334e6ad245a8041f387f6e0fc3f87/ts/Extensions/PatternFill.ts#L99-L121
      'M 0 10 L 10 0 M -1 1 L 1 -1 M 9 11 L 11 9',
      // Line: /
      'M 0 0 L 5 10 L 10 0',
      //ZigZag Horizontal
      'M 3 3 L 8 3 L 8 8 L 3 8 Z',
      // Squares
      'M 0 0 L 10 10 M 9 -1 L 11 1 M -1 9 L 1 11',
      // Line: \
      'M 5 5 m -4 0 a 4 4 0 1 1 8 0 a 4 4 0 1 1 -8 0' // Circles
      ]),
      dashStylePatterns: List(['Solid', 'ShortDash', 'ShortDot', 'ShortDashDot', 'ShortDashDotDot', 'Dot', 'Dash', 'LongDash', 'DashDot', 'LongDashDot', 'LongDashDotDot'])
    });
    return OrderedMap([[THEME.MULTI, multiTheme], [THEME.ORANGES, orangesTheme], [THEME.AQUAS, aquasTheme], [THEME.PURPLES, purplesTheme], [THEME.YELLOWS, yellowsTheme], [THEME.PINKS, pinksTheme], [THEME.BLUES, bluesTheme], [THEME.GREENS, greensTheme], [THEME.FUNNEL, funnelTheme], [THEME.PATTERNS, patternsTheme]]);
  }, [theme, gates]);
};
export { THEME };